import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { MonthlyExpenseSummary } from "../models/MonthlyExpenseSummary";
import { Comment } from "../models/Comment";
import { MonthlyExpenseSummaryService } from "../services/MonthlyExpenseSummaryService";
import { useAuth } from "./AuthContext";
import { useMatter } from "./MatterContext";
import { MatterService } from "../services/MatterService";
import { useSocketEventByIds } from "../hooks/useSocketEventByIds";

interface Errors {
  list?: Error | null;
  details?: Error | null;
}
interface Loadings {
  list?: boolean;
  details?: boolean;
}

interface MonthlyExpenseSummaryContextProps {
  loading: Loadings;
  error: Errors;
  monthlyExpense: MonthlyExpenseSummary | null;
  monthlyExpenseSummaries: MonthlyExpenseSummary[];
  comments: Comment[];
  createMonthlyExpenseSummary: (
    monthlyExpenseData: Omit<
      MonthlyExpenseSummary,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "updatedById"
      | "createdById"
      | "matterId"
    >,
  ) => Promise<string>;
  updateMonthlyExpenseSummary: (
    monthlyExpenseId: string,
    monthlyExpenseData: Partial<
      Omit<MonthlyExpenseSummary, "_id" | "createdAt" | "updatedAt">
    >,
  ) => Promise<void>;
  deleteMonthlyExpenseSummaryById: (monthlyExpenseId: string) => Promise<void>;
  setSelectedMonthlyExpenseSummary: (monthlyExpenseId: string) => void;
  refreshMonthlyExpenseSummary: () => Promise<void>;
}

const MonthlyExpenseSummaryContext = createContext<
  MonthlyExpenseSummaryContextProps | undefined
>(undefined);

export const useMonthlyExpenseSummary =
  (): MonthlyExpenseSummaryContextProps => {
    const context = useContext(MonthlyExpenseSummaryContext);
    if (!context) {
      throw new Error(
        "useMonthlyExpenseSummary must be used within a MonthlyExpenseSummaryProvider",
      );
    }
    return context;
  };

export const MonthlyExpenseSummaryProvider: React.FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { currentMatterId } = useMatter();
  const { user } = useAuth();

  const [monthlyExpenseSummaries, setMonthlyExpenseSummarySummaries] = useState<
    MonthlyExpenseSummary[]
  >([]);
  const [monthlyExpense, setMonthlyExpenseSummary] =
    useState<MonthlyExpenseSummary | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState<Loadings>({});
  const [error, setError] = useState<Errors>({});

  const fetchMonthlyExpenseSummarySummaries = async () => {
    if (!user || !currentMatterId) return;
    try {
      const docs =
        await MatterService.getMonthlyExpenseSummaries(currentMatterId);
      setMonthlyExpenseSummarySummaries(docs);
      setError((prev) => ({ ...prev, list: null }));
    } catch (err: any) {
      setError((prev) => ({ ...prev, list: err }));
    } finally {
      setLoading((prev) => ({ ...prev, list: false }));
    }
  };

  const fetchComments = async (monthlyExpenseId: string) => {
    try {
      const commentList =
        await MonthlyExpenseSummaryService.getComments(monthlyExpenseId);
      setComments(commentList);
    } catch (err) {
      console.error("Failed to fetch monthlyExpense comments:", err);
    }
  };

  useEffect(() => {
    setLoading((prev) => ({ ...prev, list: true }));
    fetchMonthlyExpenseSummarySummaries();
  }, [user, currentMatterId]);

  useSocketEventByIds("monthlyexpensesummaries:changed", ["*"], () => {
    fetchMonthlyExpenseSummarySummaries();
  });

  const setSelectedMonthlyExpenseSummary = (monthlyExpenseId: string) => {
    const selected =
      monthlyExpenseSummaries.find((d) => d._id === monthlyExpenseId) || null;
    setMonthlyExpenseSummary(selected);
    if (selected) fetchComments(selected._id);
  };

  const createMonthlyExpenseSummary = async (
    monthlyExpenseData: Omit<
      MonthlyExpenseSummary,
      | "_id"
      | "createdAt"
      | "updatedAt"
      | "updatedById"
      | "createdById"
      | "matterId"
    >,
  ): Promise<string> => {
    if (!currentMatterId) return "";
    const newId =
      await MonthlyExpenseSummaryService.createMonthlyExpenseSummary({
        ...monthlyExpenseData,
        matterId: currentMatterId,
      });
    return newId;
  };

  const updateMonthlyExpenseSummary = async (
    monthlyExpenseId: string,
    updatedData: Partial<
      Omit<MonthlyExpenseSummary, "_id" | "createdAt" | "updatedAt">
    >,
  ) => {
    await MonthlyExpenseSummaryService.updateMonthlyExpenseSummary(
      monthlyExpenseId,
      {
        ...updatedData,
        matterId: currentMatterId!,
      },
    );
  };

  const deleteMonthlyExpenseSummaryById = async (monthlyExpenseId: string) => {
    if (!currentMatterId) return;
    await MonthlyExpenseSummaryService.deleteMonthlyExpenseSummary(
      currentMatterId,
      monthlyExpenseId,
    );
    fetchMonthlyExpenseSummarySummaries();
  };

  const refreshMonthlyExpenseSummary = async () => {
    if (!currentMatterId) return;
    setLoading((prev) => ({ ...prev, list: true }));
    try {
      await MatterService.refreshMonthlyExpenseSummaries(currentMatterId);
      await fetchMonthlyExpenseSummarySummaries();
    } catch (err) {
      console.error("Failed to refresh monthly expense summary:", err);
    } finally {
      setLoading((prev) => ({ ...prev, list: false }));
    }
  };

  return (
    <MonthlyExpenseSummaryContext.Provider
      value={{
        loading,
        error,
        monthlyExpense,
        monthlyExpenseSummaries,
        comments,
        createMonthlyExpenseSummary,
        updateMonthlyExpenseSummary,
        deleteMonthlyExpenseSummaryById,
        setSelectedMonthlyExpenseSummary,
        refreshMonthlyExpenseSummary,
      }}
    >
      {children}
    </MonthlyExpenseSummaryContext.Provider>
  );
};
